/**
 * Presentation helpers for the BI sessions table.
 *
 * A "session" row comes from Console `/bi/user-activity` (`kind: "sessions"`),
 * which Platform builds in SQL from Hub telemetry (`customer_events`) and
 * sessionized legacy V1 `user_actions`. Raw events of one Hub session come
 * from `/platform-proxy/api/v2/customer-events/session/{id}`.
 */

export const SOURCE_LABELS = {
  hub_v2: "Hub",
  hub_v1: "Hub V1",
  login: "Login only",
};

export const SOURCE_BADGE_CLASS = {
  hub_v2: "bg-accent-purple/10 text-accent-purple",
  hub_v1: "bg-surface-neutral-subtle text-text-secondary",
  login: "bg-accent-green/10 text-accent-green",
};

export const ACTION_LABELS = {
  done: "done",
  discarded: "discarded",
  shared: "shared",
  drilled_in: "drilled in",
  assigned: "assigned",
  claimed: "claimed",
  external_click: "external link",
};

export function formatDuration(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return "—";
  const total = Math.max(0, Math.round(Number(ms) / 1000));
  if (total < 1) return "< 1s";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

/** "+m:ss" offset of an event from the session start. */
export function eventOffsetLabel(startIso, eventIso) {
  const start = parseTs(startIso);
  const at = parseTs(eventIso);
  if (!start || !at) return "";
  const sec = Math.max(0, Math.round((at - start) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `+${m}:${String(s).padStart(2, "0")}`;
}

/** Platform timestamps may lack an offset (naive UTC) — parse as UTC, never local. */
export function parseTs(value) {
  if (!value) return null;
  const s = String(value).trim();
  const hasZone = /[zZ]$|[+-]\d{2}:\d{2}$/.test(s);
  const d = new Date(hasZone ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Human summary of the `action_counts` map: "2 done · 1 discarded". */
export function describeActions(actionCounts) {
  const entries = Object.entries(actionCounts || {}).filter(([, n]) => n > 0);
  if (!entries.length) return "";
  return entries.map(([key, n]) => `${n} ${ACTION_LABELS[key] || key.replace(/_/g, " ")}`).join(" · ");
}

const pick = (props, key) => (props && props[key] != null && props[key] !== "" ? props[key] : null);

// Hub V2 routes (ZenytPlatformFrontend v2/src/app/router.tsx) — mirrors Platform
// `bi_activity.page_label`, but with entity titles when `entities` are available.
const PATH_GROUP = /^\/dashboard\/findings\/([^/?#]+)(?:\/details\/([^/?#]+))?/;
const PATH_LEGACY_RUN = /^\/runs\/legacy-run-([^/?#]+)/;
const PATH_RUN = /^\/runs\/([^/?#]+)/;
const PATH_AGENT_LAUNCH = /^\/agents\/([^/?#]+)\/launch/;
const STATIC_PAGES = {
  "/": "Overview",
  "/overview": "Overview",
  "/dashboard": "Overview",
  "/findings": "Findings",
  "/activity": "Activity",
  "/agents": "Agents",
  "/settings": "Settings",
};

const cleanPath = (path) => {
  if (!path || typeof path !== "string") return null;
  let p = path.split("?")[0].split("#")[0].trim();
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p || null;
};

const titleOf = (map, id) => (map && id && map[id] && map[id].title) || null;
const urlOf = (map, id) => (map && id && map[id] && map[id].url) || null;

/** Absolute Hub link for a raw Hub pathname (navigation events), or null without a base. */
export function hubPathUrl(hubUrl, path) {
  const p = cleanPath(path);
  if (!hubUrl || !p) return null;
  return `${String(hubUrl).replace(/\/+$/, "")}${p.startsWith("/") ? p : `/${p}`}`;
}

const groupBySlug = (entities, slug, websiteId) => {
  const slugs = entities?.group_slugs || {};
  if (websiteId && slugs[`${websiteId}|${slug}`]) return slugs[`${websiteId}|${slug}`];
  const key = Object.keys(slugs).find((k) => k.endsWith(`|${slug}`));
  return key ? slugs[key] : null;
};

/**
 * Readable name for a Hub pathname: "Overview", "Finding group: Price mismatch",
 * "Run: Checkout flow", "Scan: PDP audit", "Agent launch: Pricing Agent".
 */
export function pageLabel(path, entities, websiteId) {
  const p = cleanPath(path);
  if (!p) return null;
  if (STATIC_PAGES[p]) return STATIC_PAGES[p];
  let m = p.match(PATH_GROUP);
  if (m) {
    const groupId = groupBySlug(entities, m[1], websiteId);
    const groupTitle = titleOf(entities?.groups, groupId);
    if (m[2]) {
      const findingTitle = titleOf(entities?.findings, m[2]);
      return `Finding: ${findingTitle || m[2]}${groupTitle ? ` (${groupTitle})` : ""}`;
    }
    return `Finding group: ${groupTitle || m[1]}`;
  }
  m = p.match(PATH_LEGACY_RUN);
  if (m) return `Scan: ${titleOf(entities?.scans, m[1]) || m[1]}`;
  m = p.match(PATH_RUN);
  if (m) {
    const run = entities?.runs?.[m[1]];
    const agent = run ? titleOf(entities?.agents, run.agent_id) : null;
    return `Run: ${run?.title || m[1]}${agent ? ` · ${agent}` : ""}`;
  }
  m = p.match(PATH_AGENT_LAUNCH);
  if (m) {
    const agentId = entities?.agent_slugs?.[m[1]];
    return `Agent launch: ${titleOf(entities?.agents, agentId) || m[1]}`;
  }
  return p;
}

/**
 * Chips for the entities a session touched: [{kind, id, label, href}]
 * (agents, runs, scans, groups, findings). `href` is the Hub page when Platform
 * supplied one (it builds them from its own FRONTEND_URL), else null.
 */
export function entityChips(session, entities) {
  const refs = session?.refs || {};
  const out = [];
  const push = (kind, ids, map, fmt) => {
    (ids || []).forEach((id) => {
      const row = map?.[id];
      if (row) out.push({ kind, id, label: fmt ? fmt(row) : row.title || id, href: row.url || null });
    });
  };
  push("agent", refs.agents, entities?.agents);
  push("run", refs.runs, entities?.runs, (r) => r.title || "Run");
  push("scan", refs.scans, entities?.scans, (r) => r.title || "Scan");
  push("group", refs.groups, entities?.groups);
  push("finding", refs.findings, entities?.findings);
  return out;
}

/**
 * Human label + detail for one raw customer_event. Unknown types fall back to the raw name.
 * Returns `{ label, detail, href, id }`: `href` is the Hub page the event is about
 * (navigation target, run, finding group, finding) when links are available;
 * `id` is the raw identifier behind the title so operators can correlate.
 */
export function describeEvent(event, entities, hubUrl) {
  // Uniform shape regardless of which branch produced it.
  return { href: null, id: null, ...describeEventRaw(event, entities, hubUrl) };
}

function describeEventRaw(event, entities, hubUrl) {
  const type = event?.event_type || "";
  const p = event?.properties || {};
  const wid = event?.website_id;
  const groupTitle = titleOf(entities?.groups, event?.group_id);
  const findingTitle = titleOf(entities?.findings, event?.finding_id);
  const subject = findingTitle || groupTitle || null;
  const subjectHref = urlOf(entities?.findings, event?.finding_id) || urlOf(entities?.groups, event?.group_id);
  const subjectId = (findingTitle && event.finding_id) || (groupTitle && event.group_id) || null;
  const withSubject = (label, detail) => ({ label, detail, href: subjectHref, id: subjectId });
  switch (type) {
    case "journey.session_start":
      return {
        label: "Session started",
        detail: pageLabel(pick(p, "landing_page"), entities, wid),
        href: hubPathUrl(hubUrl, pick(p, "landing_page")),
        id: null,
      };
    case "journey.session_end":
      return {
        label: "Session ended",
        detail: p.total_duration_ms != null ? `${formatDuration(p.total_duration_ms)} · ${p.pages_viewed ?? 0} pages · ${p.findings_acted_on ?? 0} actions` : null,
      };
    case "journey.navigation":
      return {
        label: "Navigated",
        detail:
          [pageLabel(pick(p, "from_surface"), entities, wid), pageLabel(pick(p, "to_surface"), entities, wid)]
            .filter(Boolean)
            .join(" → ") || null,
        href: hubPathUrl(hubUrl, pick(p, "to_surface")),
        id: null,
      };
    case "journey.finding_reached":
      return withSubject("Reached a finding", [subject, pick(p, "entry_path")].filter(Boolean).join(" · ") || null);
    case "journey.time_to_first_action":
      return { label: "First action", detail: p.action_type ? `${p.action_type} after ${formatDuration(p.elapsed_ms)}` : null };
    case "journey.loaded_more":
      return { label: "Loaded more", detail: pick(p, "surface"), href: null, id: null };
    case "ranking.viewed":
      return { label: "Viewed ranking", detail: p.count != null ? `${p.count} groups` : null };
    case "ranking.position_clicked":
      return { label: "Clicked ranking position", detail: p.position != null ? `#${p.position}` : null };
    case "ranking.reordered":
      return { label: "Ranking reordered", detail: pick(p, "trigger"), href: null, id: null };
    case "finding_group.opened": {
      const via = p.position_in_ranking != null ? `#${p.position_in_ranking} via ${p.entry_path || "?"}` : pick(p, "entry_path");
      return withSubject("Opened finding group", [groupTitle, via].filter(Boolean).join(" · ") || null);
    }
    case "finding_group.closed":
      return withSubject("Closed finding group", [groupTitle, p.duration_ms != null ? `after ${formatDuration(p.duration_ms)}` : null].filter(Boolean).join(" · ") || null);
    case "finding.drilled_in":
      return withSubject("Drilled into finding", [subject, pick(p, "from_surface")].filter(Boolean).join(" · ") || null);
    case "finding.marked_done":
      return withSubject("Marked done", [subject, pick(p, "category_slug"), p.is_bulk ? "bulk" : null].filter(Boolean).join(" · ") || null);
    case "finding.discarded":
      return withSubject("Discarded", [subject, [pick(p, "discard_reason"), pick(p, "discard_reason_text")].filter(Boolean).join(" — ") || null].filter(Boolean).join(" · ") || null);
    case "finding.shared":
      return withSubject("Shared", [subject, pick(p, "share_target"), p.is_bulk ? `bulk (${p.count ?? "?"})` : null].filter(Boolean).join(" · ") || null);
    case "finding.assigned":
      return withSubject("Assigned", [subject, pick(p, "assignee_id")].filter(Boolean).join(" · ") || null);
    case "finding.claimed":
      return withSubject("Claimed", subject);
    case "finding.example_expanded":
      return withSubject("Expanded example", [subject, p.example_index != null ? `${p.example_index + 1} of ${p.total_examples ?? "?"}` : null].filter(Boolean).join(" · ") || null);
    case "finding.external_url_clicked":
      return { label: "Opened external link", detail: pick(p, "target_url"), href: pick(p, "target_url"), id: null };
    case "finding.time_spent_ms":
      return withSubject("Time on finding", [subject, formatDuration(p.duration_ms)].filter(Boolean).join(" · "));
    case "activity.viewed":
      return { label: "Viewed activity", detail: p.runs_visible != null ? `${p.runs_visible} runs visible` : null, href: hubPathUrl(hubUrl, "/activity"), id: null };
    case "activity.run_clicked": {
      const run = entities?.runs?.[p.run_id];
      const agentId = run?.agent_id || entities?.agent_slugs?.[p.agent_id];
      const agent = titleOf(entities?.agents, agentId) || (p.agent_id && p.agent_id !== "unknown" ? p.agent_id : null);
      return {
        label: "Opened run",
        detail: [run?.title || pick(p, "run_id"), agent, pick(p, "status"), p.finding_count != null ? `${p.finding_count} findings` : null]
          .filter(Boolean)
          .join(" · ") || null,
        href: run?.url || (p.run_id ? hubPathUrl(hubUrl, `/runs/${p.run_id}`) : null),
        id: pick(p, "run_id"),
      };
    }
    case "overview.scroll_depth":
      return { label: "Scrolled overview", detail: p.max_position_viewed != null ? `to #${p.max_position_viewed}` : null };
    case "overview.time_spent_ms":
    case "findings.time_spent_ms":
    case "activity.time_spent_ms":
      return { label: `Time on ${type.split(".")[0]}`, detail: formatDuration(p.duration_ms), href: null, id: null };
    case "release_announcement.viewed":
      return { label: "Saw release announcement", detail: null, href: null, id: null };
    case "release_announcement.dismissed":
      return { label: "Dismissed release announcement", detail: null, href: null, id: null };
    case "release_announcement.blog_clicked":
      return { label: "Opened release blog post", detail: null, href: null, id: null };
    case "release_announcement.video_played":
      return { label: "Played release video", detail: null, href: null, id: null };
    default:
      return { label: type || "event", detail: null };
  }
}

export function isActionEvent(eventType) {
  return [
    "finding.marked_done",
    "finding.discarded",
    "finding.shared",
    "finding.drilled_in",
    "finding.assigned",
    "finding.claimed",
    "finding.external_url_clicked",
  ].includes(eventType);
}

/** Roll-up strip for the loaded sessions (client side; the page holds ≤ 1000). */
export function summarizeSessions(sessions) {
  const list = Array.isArray(sessions) ? sessions : [];
  const users = new Set();
  let withActions = 0;
  let totalActions = 0;
  let pageViews = 0;
  const durations = [];
  for (const s of list) {
    if (s.user_id) users.add(s.user_id);
    if ((s.actions || 0) > 0) withActions += 1;
    totalActions += s.actions || 0;
    pageViews += s.page_views || 0;
    if (s.source === "hub_v2" && s.duration_ms != null) durations.push(s.duration_ms);
  }
  durations.sort((a, b) => a - b);
  const medianDurationMs = durations.length
    ? durations.length % 2
      ? durations[(durations.length - 1) / 2]
      : (durations[durations.length / 2 - 1] + durations[durations.length / 2]) / 2
    : null;
  return {
    count: list.length,
    users: users.size,
    withActions,
    totalActions,
    pageViews,
    medianDurationMs,
    actionRate: list.length ? withActions / list.length : 0,
  };
}
